import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * Ambient-brightness meter — a gradient fill bar + a percentage label, matching
 * the Operations design's illumination widget. Accepts a 0–1 fraction (as the
 * API stores it) or a 0–100 percentage; values are clamped.
 *
 *   <app-illumination-bar [value]="0.8" />
 */
@Component({
  selector: 'app-illumination-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex items-center gap-3">
      <div class="h-2 flex-1 overflow-hidden rounded-full bg-surface-2 shadow-raised">
        <div class="illum-fill h-full rounded-full" [style.width.%]="pct()"></div>
      </div>
      <span class="min-w-[40px] text-right font-num text-[14px] font-semibold text-ink-2">
        {{ pct() }}%
      </span>
    </div>
  `,
  styles: `
    /* Grey→yellow gradient (ambient brightness), token-driven endpoints. */
    .illum-fill {
      background: linear-gradient(90deg, var(--color-surface-3), var(--color-caution));
    }
  `,
})
export class IlluminationBar {
  /** 0–1 fraction or 0–100 percentage. */
  readonly value = input.required<number>();

  protected readonly pct = computed(() => {
    const v = this.value();
    const asPct = v <= 1 ? v * 100 : v;
    return Math.round(Math.min(100, Math.max(0, asPct)));
  });
}
